import { timeStringToMs } from './utility/DateFormatter';
import { EpgApi } from '../api/EpgApi';
import { Log } from '@lightningjs/sdk';
import { IProgramSummary, IScheduleSummary, ISeasonSummary, ISeriesSummary } from '../api/models/AndromeTV.types';
import { Backend } from '../api/Backend';

export interface ScheduleTimes {
  from: Date;
  until: Date;
}

export interface ScheduleData {
  schedule: IScheduleSummary;
  program: IProgramSummary;
  season?: ISeasonSummary;
  series?: ISeriesSummary;
}

export class EpgScheduleHelper {
  /**
   * @param {Backend} backend
   * @param {string} channelId
   * @returns {Promise<ScheduleData>}
   */
  static getCurrentScheduleOfChannel(backend: Backend, channelId: string): Promise<ScheduleData | undefined> {
    const scheduleTimes = EpgScheduleHelper.getTodayFromUntil();
    return EpgApi.channelSchedules(backend, channelId, scheduleTimes.from, scheduleTimes.until).then(result => {
      const now = Date.now();
      const currentSchedule = result.schedules?.find(s => {
        const start = timeStringToMs(s.published.start);
        const end = timeStringToMs(s.published.end);
        return now >= start && now <= end;
      });
      if (result.programs && currentSchedule) {
        const program = EpgScheduleHelper.findProgram(result.programs, currentSchedule.program);
        let season: ISeasonSummary | undefined;
        let series: ISeriesSummary | undefined;
        if (program?.season && result.seasons) {
          season = EpgScheduleHelper.findSeason(result.seasons, program.season);
        }
        if (season?.series && result.series) {
          series = EpgScheduleHelper.findSeries(result.series, season.series);
        }

        if (program) {
          Log.info('Current schedule found', currentSchedule);
          const res: ScheduleData = { schedule: currentSchedule, program: program, season: season, series: series };
          return res;
        }
      }
      return undefined;
    });
  }

  /**
   * @returns {ScheduleTimes}
   */
  static getTodayFromUntil(): ScheduleTimes {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return {
      from: today,
      until: tomorrow,
    };
  }

  /**
   * @param {IProgramSummary[]} programs
   * @param {string} programId
   * @returns {IProgramSummary}
   */
  static findProgram(programs: IProgramSummary[], programId: string): IProgramSummary | undefined {
    return programs.find(p => p.id === programId);
  }

  static findSeason(seasons: ISeasonSummary[], seasonId: string): ISeasonSummary | undefined {
    return seasons.find(s => s.id === seasonId);
  }

  static findSeries(series: ISeriesSummary[], seriesId: string): ISeriesSummary | undefined {
    return series.find(s => s.id === seriesId);
  }

  /**
   * @param {Backend} backend
   * @param {string} channelId
   * @returns {Promise<ScheduleData>}
   */
  static getReplaySchedule(backend: Backend, channelId: string): Promise<ScheduleData | undefined> {
    const scheduleTimes = EpgScheduleHelper.getTodayFromUntil();
    return EpgApi.channelSchedules(backend, channelId, scheduleTimes.from, scheduleTimes.until).then(result => {
      const now = Date.now();
      const replaySchedules = result.schedules?.filter(s => {
        const end = timeStringToMs(s.published.end);
        return now > end;
      });
      if (replaySchedules) {
        const selectedSchedule = replaySchedules[1];
        if (result.programs && selectedSchedule) {
          const program = EpgScheduleHelper.findProgram(result.programs, selectedSchedule.program);

          if (program) {
            const res: ScheduleData = { schedule: selectedSchedule, program: program };
            return res;
          }
        }
      }
      return undefined;
    });
  }
}
